import React, { useEffect, useState } from "react";
import {
  Link
} from "react-router-dom";
import {
  Plus,
  Pencil,
  Power,
  Layers
} from "lucide-react";
import * as ownerApi from "../../api/payroll/salaryComponentApi";
import * as portalApi from "../../api/payroll/salaryComponentPortalApi";
import {
  Button,
  IconButton,
  Card,
  CardContent,
  Table,
  Badge,
  EmptyState
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

export default function SalaryComponentList({ isPortal = false }) {
  const api = isPortal ? portalApi : ownerApi;
  const basePath = isPortal ? "/staff-panel/salary-components" : "/salary-components";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.getAllSalaryComponents();
        if (cancelled) return;
        setItems(Array.isArray(res) ? res : res.data || []);
      } catch (err) {
        if (!cancelled) toast.error(err.response?.data?.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPortal]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getAllSalaryComponents();
      setItems(Array.isArray(res) ? res : res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      const res = await api.toggleSalaryComponentActive(id);
      toast.success(res.message || "Updated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Toggle failed");
    }
  };

  // ---- Presentation-only derivations below (no data/logic changes) ----

  const tableRows = items.map((item) => ({ ...item, id: item._id }));

  const columns = [
    {
      key: "name",
      header: "Name",
      render: (item) => <span className="font-medium text-neutral-900 dark:text-neutral-100">{item.name}</span>,
    },
    {
      key: "type",
      header: "Type",
      render: (item) => (
        <Badge variant={item.type === "EARNING" ? "success" : "danger"}>{item.type}</Badge>
      ),
    },
    {
      key: "calculationType",
      header: "Calculation",
      render: (item) => <span className="text-xs text-neutral-600 dark:text-neutral-300">{item.calculationType}</span>,
    },
    { key: "defaultValue", header: "Default" },
    {
      key: "isStatutory",
      header: "Statutory",
      render: (item) => (item.isStatutory ? "Yes" : "No"),
    },
    {
      key: "status",
      header: "Status",
      render: (item) => <Badge variant={item.active ? "success" : "default"}>{item.active ? "Active" : "Inactive"}</Badge>,
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      nowrap: true,
      render: (item) => (
        <div className="flex items-center justify-end gap-1">
          <Link to={`${basePath}/edit/${item._id}`}>
            <IconButton aria-label="Edit salary component" variant="ghost" size="sm">
              <Pencil className="h-4 w-4" aria-hidden="true" />
            </IconButton>
          </Link>
          <IconButton
            aria-label={item.active ? "Deactivate component" : "Activate component"}
            variant="ghost"
            size="sm"
            onClick={() => handleToggle(item._id)}
            className="hover:bg-warning-50 hover:text-warning-700"
          >
            <Power className="h-4 w-4" aria-hidden="true" />
          </IconButton>
        </div>
      ),
    },
  ];

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 border-b border-neutral-200 dark:border-neutral-800 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-100 sm:text-2xl">
          Salary Components
        </h1>
        <Link to={`${basePath}/create`}>
          <Button variant="primary" leftIcon={<Plus className="h-4 w-4" />}>
            Add Component
          </Button>
        </Link>
      </div>

      
      

      <Card>
        <CardContent className="p-0">
          <Table
            columns={columns}
            data={tableRows}
            isLoading={loading}
            emptyState={
              <EmptyState
                icon={Layers}
                title="No salary components yet"
                description="Add your first component to start building salary structures."
                action={
                  <Link to={`${basePath}/create`}>
                    <Button variant="primary" size="sm" leftIcon={<Plus className="h-4 w-4" />}>
                      Add Component
                    </Button>
                  </Link>
                }
              />
            }
          />
        </CardContent>
      </Card>
    </div>
  );
}